/**
 * User-facing labels for workflow step function values (ACME-1673).
 * Grids and the step dropdown use the display helpers.
 * Template load uses mapLegacyLambdaFunctionName so only legacy Lambda aliases are rewritten.
 */

const LEGACY_LAMBDA_TO_UI: Record<string, string> = {
  filemove: "filecopy",
  filepolling: "filedownload",
  filezip: "filezip",
  fileencrypt: "fileencrypt",
  filedelete: "filedelete",
  filetransferrule: "rulechecker",
};

const DISPLAY_LABELS: Record<string, string> = {
  filecopy: "Copy",
  filedownload: "Download",
  filezip: "Compress",
  fileencrypt: "Encrypt",
  filedelete: "Delete",
  unprocessedfilearchive: "Unprocessed file archive",
  rulechecker: "Rule checker",
};

/** Dropdown labels the wizard and automation type, mapped back to stored tokens. */
const LABEL_TO_CANONICAL: Record<string, string> = {
  copy: "filecopy",
  download: "filedownload",
  compress: "filezip",
  encrypt: "fileencrypt",
  delete: "filedelete",
  "unprocessed file archive": "unprocessedfilearchive",
  "rule checker": "rulechecker",
};

/**
 * Map stored Lambda names to canonical UI functionName values (WorkflowStepFunction.cs).
 * Legacy aliases normalize to lowercase UI tokens; unknown names keep original casing.
 */
export function normalizeStepFunctionValue(value: string): string {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return "";
  }
  const lower = trimmed.toLowerCase();
  if (LEGACY_LAMBDA_TO_UI[lower] !== undefined) {
    return LEGACY_LAMBDA_TO_UI[lower];
  }
  if (DISPLAY_LABELS[lower] !== undefined) {
    return lower;
  }
  if (LABEL_TO_CANONICAL[lower] !== undefined) {
    return LABEL_TO_CANONICAL[lower];
  }
  return trimmed;
}

/**
 * Legacy Lambda token to the UI token stored on template load.
 * Anything that is not a legacy alias is returned unchanged, including casing.
 */
export function mapLegacyLambdaFunctionName(lambdaName: string): string {
  if (lambdaName == null) {
    return lambdaName as any;
  }
  const key = lambdaName.toLowerCase();
  if (LEGACY_LAMBDA_TO_UI[key] !== undefined) {
    return LEGACY_LAMBDA_TO_UI[key];
  }
  return lambdaName;
}

function humanizeFallback(canonical: string): string {
  if (!canonical) {
    return "";
  }
  const spaced = canonical
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  const words = spaced.split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "";
  }
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Friendly label for grids and read-only text; accepts raw or legacy stored values. */
export function getStepFunctionDisplayLabel(value: string): string {
  const canonical = normalizeStepFunctionValue(value);
  if (!canonical) {
    return "";
  }
  const known = DISPLAY_LABELS[canonical];
  if (known) {
    return known;
  }
  return humanizeFallback(canonical);
}

export type StepFunctionSelectOption = { name: string; value: string };

/** Build jha-form-select options from FUNCTION_TYPE API list (mixed casing). */
export function mapStepFunctionOptions(list: string[]): StepFunctionSelectOption[] {
  const seen = new Set<string>();
  const options: StepFunctionSelectOption[] = [];
  for (const entry of list ?? []) {
    const value = normalizeStepFunctionValue(String(entry));
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({
      value,
      name: getStepFunctionDisplayLabel(value),
    });
  }
  return options;
}
